/**
 * Admin Dashboard Data Hook
 * 
 * Provides comprehensive statistics and data for the admin dashboard. Fetches
 * and aggregates key metrics across users, jobs, bids, disputes, and vendor
 * verifications to give admins a high-level view of platform health and activity.
 * 
 * **Dashboard Metrics:**
 * - Pending Vendors: Count of vendors awaiting verification
 * - Active Disputes: Number of open or under-review disputes
 * - Total Users: Platform-wide user count
 * - Total Jobs: Count of non-deleted jobs
 * - Total Bids: Count of all bids submitted
 * - Completion Rate: Percentage of jobs completed
 * 
 * **Vendor Verification Management:**
 * - Fetches verification requests by status
 * - Tracks verification level (basic, verified, premium)
 * - Provides approval/rejection workflow
 * - Updates vendor profiles upon approval
 * - Maintains audit trail of reviews
 * 
 * **Real-Time Updates:**
 * - Subscribes to verification_requests table changes
 * - Auto-refreshes stats when verifications are processed
 * - Maintains data consistency across admin sessions
 * 
 * **Key Features:**
 * - Comprehensive platform statistics
 * - Vendor verification workflow
 * - Real-time subscription updates
 * - Pending vendor management
 * - Dispute tracking
 * - Performance metrics
 * 
 * **Use Cases:**
 * - Admin dashboard overview
 * - Vendor verification management
 * - Platform health monitoring
 * - Activity tracking and reporting
 * - Quality control workflows
 * 
 * @returns {Object} Admin dashboard data and management functions
 * @returns {AdminStats|null} stats - Platform-wide statistics
 * @returns {any[]} pendingVendors - Vendors awaiting verification
 * @returns {any[]} verificationRequests - Pending verification requests
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 * @returns {Function} refetch - Manually refresh all data
 * @returns {Function} approveVendor - Approve/reject vendor
 * @returns {Function} approveVerificationRequest - Process verification request
 * 
 * @example
 * // Display admin dashboard stats
 * const { stats, loading, error } = useAdminData();
 * 
 * if (loading) return <Spinner />;
 * if (error) return <Alert>{error}</Alert>;
 * 
 * return (
 *   <Grid>
 *     <StatCard title="Pending Verifications" value={stats.pendingVendors} />
 *     <StatCard title="Active Disputes" value={stats.activeDisputes} />
 *     <StatCard title="Total Users" value={stats.totalUsers} />
 *     <StatCard title="Completion Rate" value={`${stats.completionRate}%`} />
 *   </Grid>
 * );
 * 
 * @example
 * // Process verification request
 * const { verificationRequests, approveVerificationRequest } = useAdminData();
 * 
 * const handleApprove = async (requestId: string, approved: boolean) => {
 *   try {
 *     await approveVerificationRequest(
 *       requestId, 
 *       approved,
 *       approved ? 'All requirements met' : 'Missing required documentation'
 *     );
 *     toast({ 
 *       title: approved ? 'Vendor Approved' : 'Request Rejected' 
 *     });
 *   } catch (error) {
 *     toast({ 
 *       title: 'Error', 
 *       description: error.message,
 *       variant: 'destructive' 
 *     });
 *   }
 * };
 * 
 * @example
 * // Monitor platform health
 * const { stats } = useAdminData();
 * 
 * const healthScore = calculateHealthScore({
 *   completionRate: stats.completionRate,
 *   activeDisputes: stats.activeDisputes,
 *   totalJobs: stats.totalJobs
 * });
 * 
 * <HealthIndicator 
 *   score={healthScore}
 *   alert={stats.activeDisputes > 5}
 * />
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminStats {
  pendingVendors: number;
  activeDisputes: number;
  totalUsers: number;
  totalJobs: number;
  totalBids: number;
  completionRate: number;
}

interface VendorApplication {
  id: string;
  user_id: string;
  organization_id: string;
  categories: string[];
  location?: string;
  typical_budget_min_cents?: number;
  typical_budget_max_cents?: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    user_id: string;
    organization_id: string;
    role?: string;
  };
}

interface UseAdminDataOptions {
  includeTestData?: boolean;
}

export const useAdminData = (options: UseAdminDataOptions = {}) => {
  const { includeTestData = false } = options;
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [pendingVendors, setPendingVendors] = useState<any[]>([]);
  const [verificationRequests, setVerificationRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAdminStats = async () => {
    try {
      // Get test organization IDs to exclude if not including test data
      let testOrgIds: string[] = [];
      if (!includeTestData) {
        const { data: testOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('is_test', true);
        testOrgIds = testOrgs?.map(o => o.id) || [];
      }

      // Get pending verification requests count
      const { count: pendingCount } = await supabase
        .from('verification_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Get active disputes count (filter by org if needed)
      let disputeQuery = supabase
        .from('disputes')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'under_review']);
      if (testOrgIds.length > 0) {
        disputeQuery = disputeQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: disputeCount } = await disputeQuery;

      // Get total users count (filter by org if needed)
      let userQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      if (testOrgIds.length > 0) {
        userQuery = userQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: userCount } = await userQuery;

      // Get total jobs count (filter by org if needed)
      let jobQuery = supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false);
      if (testOrgIds.length > 0) {
        jobQuery = jobQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: jobCount } = await jobQuery;

      // Get total bids count (filter by job org if excluding test data)
      let bidCount = 0;
      if (testOrgIds.length > 0) {
        // Need to filter bids by job organization - fetch bids with job info
        const { data: bidsData } = await supabase
          .from('bids')
          .select('id, rfqs!inner(jobs!inner(organization_id))');
        const filteredBids = (bidsData || []).filter((b: any) => 
          !testOrgIds.includes(b.rfqs?.jobs?.organization_id)
        );
        bidCount = filteredBids.length;
      } else {
        const { count } = await supabase
          .from('bids')
          .select('*', { count: 'exact', head: true });
        bidCount = count || 0;
      }

      // Calculate completion rate (filter by org if needed)
      let completedQuery = supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .eq('is_deleted', false);
      if (testOrgIds.length > 0) {
        completedQuery = completedQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: completedJobs } = await completedQuery;

      const completionRate = jobCount && completedJobs ? (completedJobs / jobCount) * 100 : 0;

      setStats({
        pendingVendors: pendingCount || 0,
        activeDisputes: disputeCount || 0,
        totalUsers: userCount || 0,
        totalJobs: jobCount || 0,
        totalBids: bidCount || 0,
        completionRate: Math.round(completionRate)
      });
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
  };

  const fetchVerificationRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (error) throw error;

      const requests = (data as any[]) || [];
      const vendorProfileIds = Array.from(new Set(requests.map(r => r.vendor_profile_id).filter(Boolean)));

      let vendorMap: Record<string, any> = {};
      if (vendorProfileIds.length > 0) {
        const { data: vendors } = await supabase
          .from('vendor_profiles_enhanced')
          .select(`
            id, 
            business_name, 
            business_type, 
            business_description,
            organization:organizations!vendor_profiles_enhanced_organization_id_fkey(id, name)
          `)
          .in('id', vendorProfileIds);

        if (vendors) {
          vendorMap = vendors.reduce((acc: Record<string, any>, v: any) => {
            acc[v.id] = v;
            return acc;
          }, {});
        }
      }

      const enriched = requests.map(r => ({
        ...r,
        vendor_profiles_enhanced: vendorMap[r.vendor_profile_id] || null,
      }));

      setVerificationRequests(enriched);
    } catch (err) {
      console.error('Error fetching verification requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch verification requests');
    }
  };

  const fetchPendingVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles_enhanced')
        .select('*')
        .eq('is_verified', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setPendingVendors((data as any[]) || []);
    } catch (err) {
      console.error('Error fetching pending vendors:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch vendors');
    }
  };

  const approveVerificationRequest = async (requestId: string, approved: boolean, notes?: string) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      // First get the request details
      const { data: request, error: requestError } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (requestError) throw requestError;

      // Update the verification request
      const { error: updateRequestError } = await supabase
        .from('verification_requests')
        .update({
          status: approved ? 'approved' : 'rejected',
          reviewed_by_admin_id: user.data.user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: notes || null,
        })
        .eq('id', requestId);

      if (updateRequestError) throw updateRequestError;

      // If approved, update the vendor profile
      if (approved) {
        const { error: updateVendorError } = await supabase
          .from('vendor_profiles_enhanced')
          .update({
            is_verified: true,
            verification_level: request.requested_level,
            verified_at: new Date().toISOString(),
            verified_by_admin_id: user.data.user.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', request.vendor_profile_id);

        if (updateVendorError) throw updateVendorError;
      }

      await fetchVerificationRequests();
      await fetchAdminStats();
    } catch (err) {
      console.error('Error processing verification request:', err);
      throw err;
    }
  };

  const approveVendor = async (vendorId: string, approved: boolean, notes?: string) => {
    try {
      const { error } = await supabase
        .from('vendor_profiles_enhanced')
        .update({
          is_verified: approved,
          verification_level: approved ? 'verified' : 'none',
          verified_at: approved ? new Date().toISOString() : null,
          verified_by_admin_id: approved ? (await supabase.auth.getUser()).data.user?.id : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', vendorId);

      if (error) throw error;

      await fetchPendingVendors();
      await fetchAdminStats();
    } catch (err) {
      console.error('Error approving vendor:', err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchAdminStats(), fetchPendingVendors(), fetchVerificationRequests()]);
      setLoading(false);
    };

    fetchData();
  }, [includeTestData]);

  useEffect(() => {
    const channel = supabase
      .channel('admin-verification-requests')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'verification_requests' }, () => {
        fetchVerificationRequests();
        fetchAdminStats();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    stats,
    pendingVendors,
    verificationRequests,
    loading,
    error,
    refetch: () => Promise.all([fetchAdminStats(), fetchPendingVendors(), fetchVerificationRequests()]),
    approveVendor,
    approveVerificationRequest
  };
};
